import gameAudioManager from "@/utils/gameAudioManager";

/**
 * 游戏场景音频混入
 * 倒计时人声、进行中柔和欢快 BGM、一方完成危机提醒、结算音效
 */
export default {
  data() {
    return {
      crisisAudioTriggered: false,
      timeLimitWarningTriggered: false,
      // 音效设置弹窗显示状态
      soundSettingsVisible: false,
      // 音效设置（localStorage 持久化，多次使用保持）
      soundSettings: {
        bgm: "classic",
        correct: "classic",
        wrong: "classic",
      },
      bgmStyleOptions: [
        { value: "classic", label: "经典" },
        { value: "sunny", label: "欢快" },
        { value: "calm", label: "舒缓" },
        { value: "march", label: "律动" },
        { value: "off", label: "关闭" },
      ],
      correctSoundOptions: [
        { value: "classic", label: "经典" },
        { value: "chime", label: "风铃" },
        { value: "fanfare", label: "号角" },
        { value: "sparkle", label: "星光" },
      ],
      wrongSoundOptions: [
        { value: "classic", label: "经典" },
        { value: "buzz", label: "蜂鸣" },
        { value: "descend", label: "下滑" },
        { value: "thud", label: "低音" },
      ],
    };
  },
  watch: {
    gameState(newState, oldState) {
      this.handleGameStateAudio(newState, oldState);
    },
    countdownNumber(val, oldVal) {
      if (this.gameState !== "countdown") return;
      if (val === oldVal) return;
      gameAudioManager.playCountdownTick(val);
    },
    remainingTime(val, oldVal) {
      this.checkTimeLimitWarning(val, oldVal);
    },
    player1Completed(val) {
      if (val) this.checkCrisisAudio();
    },
    player2Completed(val) {
      if (val) this.checkCrisisAudio();
    },
  },
  mounted() {
    // 读取持久化的音效设置并同步到音频管理器
    this.loadSoundSettings();
    gameAudioManager.init();
    gameAudioManager.bindUnlockOnInteraction();
  },
  beforeDestroy() {
    gameAudioManager.destroy();
    this.crisisAudioTriggered = false;
    this.timeLimitWarningTriggered = false;
  },
  methods: {
    /** 从 localStorage 恢复音效设置 */
    loadSoundSettings() {
      try {
        const saved = JSON.parse(window.localStorage.getItem("pk_sound_settings"));
        if (saved && typeof saved === "object") {
          this.soundSettings = { ...this.soundSettings, ...saved };
        }
      } catch (err) {
        /* 解析失败使用默认值 */
      }
      // 旧版本变速风格值统一映射为经典
      const legacyBgm = { standard: "classic", lively: "classic", gentle: "classic" };
      if (legacyBgm[this.soundSettings.bgm]) {
        this.soundSettings.bgm = legacyBgm[this.soundSettings.bgm];
      }
      gameAudioManager.setSoundConfig(this.soundSettings);
    },

    /**
     * 切换音效风格并试听
     * @param {'bgm'|'correct'|'wrong'} kind
     * @param {string} value 风格值
     */
    selectSoundStyle(kind, value) {
      if (this.soundSettings[kind] === value) {
        // 重复点击仅试听，不重复写存储
        gameAudioManager.previewSound(kind, value);
        return;
      }
      this.soundSettings[kind] = value;
      window.localStorage.setItem("pk_sound_settings", JSON.stringify(this.soundSettings));
      gameAudioManager.setSoundConfig({ [kind]: value });
      gameAudioManager.previewSound(kind, value);
    },

    handleGameStateAudio(newState, oldState) {
      if (newState === oldState) return;

      switch (newState) {
        case "countdown":
          this.crisisAudioTriggered = false;
          this.timeLimitWarningTriggered = false;
          gameAudioManager.unlock();
          break;
        case "playing":
          gameAudioManager.playPlayingBgmAfterStart();
          break;
        case "results":
          // 水果忍者：不播胜利音效，停止背景音乐，直接弹出胜负弹窗
          if (this.selectedQuestionType === "wordNinja") {
            gameAudioManager.stopAll();
            break;
          }
          gameAudioManager.playResults(this.winner);
          break;
        case "setup":
          gameAudioManager.stopAll();
          this.crisisAudioTriggered = false;
          this.timeLimitWarningTriggered = false;
          break;
        default:
          break;
      }
    },

    /** 任一方先完成时，提醒另一方加紧答题 */
    checkCrisisAudio() {
      if (this.gameState !== "playing") return;
      if (this.crisisAudioTriggered) return;
      // 水果忍者单人模式：无对手，不需要危机提醒（避免结束时响起循环 BGM）
      if (this.selectedQuestionType === "wordNinja") return;

      const p1Done = !!this.player1Completed;
      const p2Done = !!this.player2Completed;
      if (p1Done === p2Done) return;

      this.crisisAudioTriggered = true;
      console.info("[GameAudio] 一方已完成，播放紧迫危机提醒");
      gameAudioManager.playCrisisAlert();
    },

    /** 限时赛剩余 10 秒时提醒 */
    checkTimeLimitWarning(val, oldVal) {
      if (this.gameState !== "playing") return;
      if (this.selectedGameMode !== "timeLimit") return;
      if (this.timeLimitWarningTriggered) return;
      if (typeof oldVal !== "number" || oldVal <= 10 || val !== 10) return;

      this.timeLimitWarningTriggered = true;
      gameAudioManager.playTimeLimitWarning();
    },

    /** 任意模式答对/答错提示音 */
    playAnswerFeedback(isCorrect) {
      if (this.gameState !== "playing") return;
      if (isCorrect) {
        gameAudioManager.playAnswerCorrect();
      } else {
        gameAudioManager.playAnswerWrong();
      }
    },

    onStartGameClick() {
      gameAudioManager.unlock();
    },

    /** 首次开局 countdownNumber 已是 3 时补播，避免与 watcher 重复 */
    announceCountdownStart() {
      gameAudioManager.unlock();
      gameAudioManager.playCountdownTick(this.countdownNumber);
    },
  },
};
